import React, { useEffect, useState, useContext } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Loader from "../../../components/Loader";
import { MdDelete, MdDownload, MdArrowBack } from "react-icons/md";
import { toast } from "react-toastify";
import CustomToolTip from "../../../components/ToolTip";
import moment from "moment";

import Hoc from "../../../components/HOC";
import { EmployeeContext } from "../../../Context/Employee";
import { getEmployeeById } from "../../../Services/employee";
import SimpleTable from "../../../components/SimpleTable";

const ArchivedDocuments = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { fetchEmployeeDocuments, deleteEmployeeDocument } = useContext(EmployeeContext);
  const [documents, setDocuments] = useState([]);
  const [Loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const location = useLocation();
  const { employeeName } = location.state || {};
  const [currentEmployeeName, setCurrentEmployeeName] = useState(employeeName);

  const fetchArchivedDocumentsData = async () => {
    try {
      setLoading(true);
      const data = await fetchEmployeeDocuments(id, "Archived"); // Fetch archived documents
      console.log("Fetched archived documents:", data);
      // Reverse the order of the fetched data
      const reversedData = [...data].reverse(); // Create a shallow copy before reversing to avoid mutating original array if it's used elsewhere
      setDocuments(reversedData);
      setError(null);
    } catch (err) {
      console.error("Error fetching archived documents:", err);
      setError("Failed to fetch archived documents. Please try again.");
      toast.error("Failed to load archived documents");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchArchivedDocumentsData();

    if (!currentEmployeeName) {
      const fetchEmployee = async () => {
        try {
          const employee = await getEmployeeById(id);
          if (employee) {
            setCurrentEmployeeName(`${employee.fname || ''} ${employee.lname || ''}`.trim());
          }
        } catch (err) {
          console.error("Failed to fetch employee details:", err);
        }
      };
      fetchEmployee();
    }
  }, [id, currentEmployeeName]);

  const downloadDocument = (docData) => {
    try {
      if (!docData.downloadLink) {
        toast.error("Download link not available");
        return;
      }

      const link = window.document.createElement('a');
      link.href = docData.downloadLink;
      // Use originalFilename if available, otherwise fallback to old logic
      let filename = docData.originalFilename;
      if (!filename) {
        const extension = getFileExtension(docData.fileType);
        const documentType = docData.documentType ? docData.documentType.toLowerCase() : 'document';
        filename = `${documentType}.${extension}`;
      }
      link.download = filename;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      toast.success(`Downloading ${filename}`);
    } catch (error) {
      console.error("Error downloading document:", error);
      toast.error("Failed to download document");
    }
  };

  const getFileExtension = (mimeType) => {
    const extensions = {
      'application/pdf': 'pdf',
      'image/jpeg': 'jpg',
      'image/png': 'png',
      'image/gif': 'gif',
      'application/msword': 'doc',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
      'application/vnd.ms-excel': 'xls',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
      'text/plain': 'txt'
    };
    return extensions[mimeType] || 'file';
  };

  const handleDocumentAction = async (action, employeeId, docData) => {
    if (action === "delete") {
      const result = await deleteEmployeeDocument(employeeId, docData.documentType, "Archived"); // Pass 'Archived' status
  
      if (result.success) {
        toast.success(result.message);
        setDocuments(result.data); // update local state if needed
      } else {
        toast.error(result.message);
      }
    } else if (action === "download") {
      downloadDocument(docData);
    }
  };
  
  // Define columns for SimpleTable
  const columns = [
    {
      name: 'Document Type',
      selector: 'documentType',
      // Add className if needed, matching the original header style
      // className: 'px-6 py-3 text-left text-xs font-semibold text-[#1e293b] uppercase tracking-wider w-[25%]'
    },
    {
      name: 'Issue Date',
      selector: 'issueDate',
      // Add renderCell to format date if needed, matching the original cell rendering
      renderCell: (row) => row.issueDate ? moment(row.issueDate).format("MM-DD-YYYY") : "Not Applicable"
    },
    {
      name: 'Expiry Date',
      selector: 'expiryDate',
      // Add renderCell to format date if needed, matching the original cell rendering
      renderCell: (row) => row.expiryDate ? moment(row.expiryDate).format("MM-DD-YYYY") : "Not Applicable"
    },
    {
      name: 'Updated By',
      selector: 'updatedBy',
       renderCell: (row) => row.updatedBy || "N/A"
    },
    {
      name: 'Actions',
      selector: 'actions',
      renderCell: (row) => (
        <div className="flex items-center justify-center space-x-4">
          <button
            onClick={() => handleDocumentAction("delete", id, row)}
            className="text-red-500 hover:text-red-700 disabled:cursor-not-allowed p-2 rounded-full hover:bg-red-50 transition-colors"
          >
            <>
              <MdDelete size={20} data-tip data-for="Delete" />
              <CustomToolTip id="Delete-Archived" title="Delete" />
            </>
          </button>
          <button
            onClick={() => handleDocumentAction("download", id, row)}
            className="text-primary hover:text-blue-700 disabled:cursor-not-allowed p-2 rounded-full hover:bg-blue-50 transition-colors"
          >
            <>
              <MdDownload size={20} data-tip data-for="Download" />
              <CustomToolTip id="Download-Archived" title="Download" />
            </>
          </button>
        </div>
      ),
      headerClassName: 'text-center',
    },
  ];

  return (
    <div className="w-full px-4 pb-6 flex flex-col">
      <Loader isLoading={Loading} />
      {/* Header */}
      <div className="flex items-center gap-2 mb-6">
        <button
          type="button"
          className="text-primary hover:text-primaryDark"
          onClick={() => {
            const targetPath = `/manage-employees/uploaddocuments/${id}`;
            console.log("Navigating back to:", targetPath);
            navigate(targetPath, { state: { employeeName: employeeName } });
          }}
        >
          <MdArrowBack size={24} />
        </button>
        <h2 className="text-xl font-semibold text-primary">Archived Documents for {currentEmployeeName}</h2>
      </div>
  
      {/* Document Table */}
      <div className="bg-white rounded-lg shadow-md">
        <SimpleTable columns={columns} data={documents} />
      </div>
    </div>
  );
};

export default Hoc(ArchivedDocuments); 